import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { deleteMergeTag } from 'api/engage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

const DeleteMergeField = ({ itemId, children }: { itemId: number; children: React.ReactNode }) => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const toast = useToast();

    const { mutate: deleteItem, isPending: deleteLoading } = useMutation({
        mutationFn: () => {
            return deleteMergeTag(accountId, itemId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.mergeFields(accountId),
                type: 'all',
            });
            toast.success('Successfully deleted merge tag default!');
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                error.response?.data.message || (
                    <>
                        <span className={'font-medium'}>Whoops.</span> Something went wrong. Please
                        try again.
                    </>
                ),
            );
        },
    });

    return (
        <AlertDialog.Root type="error">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    {deleteLoading && <Loader />}
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Are you sure?</AlertDialog.Title>
                        <AlertDialog.Description>
                            Deleting a default value from a merge field will disable dynamic content
                            insertion when no data is associated with that tag.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={e => {
                                e.preventDefault();
                                deleteItem();
                            }}
                        >
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = DeleteMergeField;
const DeleteMergeFieldTrigger = AlertDialog.Trigger;
const Trigger = DeleteMergeFieldTrigger;

export {
    Root,
    Trigger, //
    DeleteMergeField,
    DeleteMergeFieldTrigger,
};
